import type { AudioAnswer } from './types';

/**
 * Recorded answers kept in IndexedDB until they are submitted, so a reload or
 * a closed tab does not lose a recording. One record per sector and language.
 */
const DB_NAME = 'yodiAudioDrafts';
const STORE = 'drafts';
const DB_VERSION = 1;

type StoredDraft = { key: string; answers: Record<string, AudioAnswer>; timestamp: number };

const draftKey = (sectorId: string, language: string) => `initial-audio:${sectorId}:${language}`;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
  });
}

function run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const request = action(db.transaction(STORE, mode).objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

export async function loadAudioDrafts(sectorId: string, language: string): Promise<Record<string, AudioAnswer>> {
  try {
    const stored = await run<StoredDraft | undefined>('readonly', (store) => store.get(draftKey(sectorId, language)));
    return stored?.answers ?? {};
  } catch (error) {
    console.warn('[initial-audio] drafts unavailable', error);
    return {};
  }
}

export async function saveAudioDrafts(
  sectorId: string,
  language: string,
  answers: Record<string, AudioAnswer>,
): Promise<void> {
  try {
    const key = draftKey(sectorId, language);
    if (Object.keys(answers).length === 0) {
      await run('readwrite', (store) => store.delete(key));
      return;
    }
    const record: StoredDraft = { key, answers, timestamp: Date.now() };
    await run('readwrite', (store) => store.put(record));
  } catch (error) {
    console.warn('[initial-audio] drafts not saved', error);
  }
}
